import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { FaEye, FaSearch, FaLock, FaTruck, FaRupeeSign, FaSyncAlt } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { fetchVerifiedOrders, fetchLiveDeliveryCharge } from '../api/api';
import OrderDetailsModal from '../components/OrderDetails/ViewOrderDetails';

const DeliveryChargePage: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isAgent, setIsAgent] = useState<boolean>(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [isBulkSyncing, setIsBulkSyncing] = useState<boolean>(false); // 🔥 Bulk Sync State
  
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);

  useEffect(() => {
    const checkUserRole = () => {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const payload = JSON.parse(decodeURIComponent(window.atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));
                if (JSON.stringify(payload).toUpperCase().includes('AGENT')) setIsAgent(true);
            }
        } catch (e) { console.error("Error decoding token", e); }
    };
    checkUserRole();
  }, []);

  const fetchDeliveryData = async () => {
    if (isAgent) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      const [webRes, verifiedRes] = await Promise.all([
          axios.get('https://www.ayushvatika.com/api/orders/all', { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => ({ data: [] })),
          fetchVerifiedOrders().catch(() => ({ data: [] }))
      ]);

      const webOrders = (webRes.data || [])
          .filter((o: any) => o.delhiveryWaybill)
          .map((o: any) => ({ 
              ...o, 
              waybill: o.delhiveryWaybill,
              customerName: `${o.user?.firstName} ${o.user?.lastName || ''}`,
              pincode: o.shippingAddress?.pincode || o.pincode || '201001',
              deliveryCharge: o.deliveryCharge || o.shippingCharge || 0,
              source: 'WEBSITE' 
          }));
      
      const crmOrders = (verifiedRes.data || [])
          .filter((o: any) => o.waybill)
          .map((o: any) => ({
              ...o,
              customerName: o.mainUser ? `${o.mainUser.firstName} ${o.mainUser.lastName || ''}` : (o.customerName || 'Unknown'),
              pincode: o.pincode || o.originalData?.pincode || '201001',
              totalAmount: o.payableAmount || 0,
              deliveryCharge: o.deliveryCharge || o.shippingCharge || 0, 
              source: 'CRM'
          }));

      const allShipped = [...webOrders, ...crmOrders];
      allShipped.sort((a, b) => new Date(b.createdAt || b.orderDate).getTime() - new Date(a.createdAt || a.orderDate).getTime());
      
      setOrders(allShipped);
    } catch (err: any) {
      toast.error("Failed to fetch delivery data!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAgent) fetchDeliveryData();
  }, [isAgent]);

  // 🔥 SINGLE SYNC
  const handleSyncCharge = async (orderId: string, pincode: string, paymentMode: string, totalAmount: number) => {
      setSyncingId(orderId);
      try {
          const codAmount = paymentMode === 'COD' ? totalAmount : 0;
          const res = await fetchLiveDeliveryCharge(pincode, 500, codAmount); 
          
          if(res.data.success && res.data.charge > 0) {
              setOrders(prev => prev.map(o => o.id === orderId ? { ...o, deliveryCharge: res.data.charge } : o));
              toast.success("Charge synced!");
          } else {
              toast.warning("Could not calculate charge. Invalid Pincode?");
          }
      } catch (err) {
          toast.error("API connection failed!");
      } finally {
          setSyncingId(null);
      }
  };

  // 🔥 BULK SYNC (Naya Function)
  const handleBulkSync = async () => {
    const pendingOrders = orders.filter(o => !o.deliveryCharge || o.deliveryCharge === 0);
    
    if (pendingOrders.length === 0) {
        toast.info("All charges are already synced!");
        return;
    }

    if (!window.confirm(`Do you want to fetch live charges for ${pendingOrders.length} orders from Delhivery?`)) return;

    setIsBulkSyncing(true);
    let successCount = 0;
    
    // We update state progressively so UI reflects the changes real-time
    for (const order of pendingOrders) {
        try {
            const codAmount = order.paymentMode === 'COD' ? order.totalAmount : 0;
            const res = await fetchLiveDeliveryCharge(order.pincode, 500, codAmount);
            if (res.data && res.data.success && res.data.charge > 0) {
                setOrders(prev => prev.map(o => o.id === order.id ? { ...o, deliveryCharge: res.data.charge } : o));
                successCount++;
            }
        } catch (error) {
            console.error(`Failed to sync ID: ${order.id}`);
        }
    }
    
    setIsBulkSyncing(false);
    toast.success(`Successfully synced ${successCount} charges!`);
  };

  const filteredOrders = useMemo(() => {
    if (!searchQuery) return orders;
    const lowerQ = searchQuery.toLowerCase();
    return orders.filter(order =>
      String(order.id).toLowerCase().includes(lowerQ) ||
      (order.waybill && order.waybill.toLowerCase().includes(lowerQ)) ||
      (order.customerName && order.customerName.toLowerCase().includes(lowerQ))
    );
  }, [orders, searchQuery]);

  const totalDeductions = useMemo(() => {
      return filteredOrders.reduce((sum, o) => sum + (Number(o.deliveryCharge) || 0), 0);
  }, [filteredOrders]);

  if (isAgent) {
      return (
          <AdminLayout>
             <div className="flex flex-col items-center justify-center min-h-[70vh] text-center">
                 <FaLock className="text-gray-300 dark:text-gray-700 text-6xl mb-4"/>
                 <h1 className="text-2xl font-black text-gray-800 dark:text-white">Access Denied</h1>
                 <p className="text-gray-500 mt-2">Delivery charge reports are restricted to Administrators.</p>
             </div>
          </AdminLayout>
      );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto p-6 min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
            <h2 className="text-3xl font-black text-gray-800 dark:text-white tracking-tight flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg text-red-600 dark:text-red-400">
                    <FaTruck size={24}/>
                </div>
                Logistics Wallet Report
            </h2>
            
            <div className="flex items-center gap-4">
                {/* 🔥 NAYA BUTTON: Bulk Sync All */}
                <button 
                    onClick={handleBulkSync}
                    disabled={isBulkSyncing || orders.filter(o => !o.deliveryCharge).length === 0}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-3 rounded-xl font-bold flex items-center gap-2 shadow-md transition-all disabled:opacity-50"
                >
                    <FaSyncAlt className={`${isBulkSyncing ? 'animate-spin' : ''}`} />
                    {isBulkSyncing ? 'Syncing Multiple...' : 'Bulk Sync Missing'}
                </button>

                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 px-4 py-2 rounded-xl flex items-center gap-3 text-orange-700 dark:text-orange-400">
                    <FaRupeeSign className="text-lg"/>
                    <div>
                        <p className="text-xs font-black uppercase tracking-widest">Total Estimated Deduction</p>
                        <p className="text-lg font-black tracking-tight">₹{totalDeductions.toFixed(2)}</p>
                    </div>
                </div>
            </div>
        </div>

        <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by Order ID, Customer Name, or AWB..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-3 w-full rounded-xl outline-none focus:ring-2 focus:ring-red-500 transition-colors bg-gray-50 border border-gray-200 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400 dark:text-gray-500" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700 transition-colors">
          <div className="overflow-x-auto">
            <table className="min-w-full leading-normal">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-4 text-left text-[11px] font-black text-gray-400 uppercase tracking-widest">Order ID</th>
                  <th className="px-6 py-4 text-left text-[11px] font-black text-gray-400 uppercase tracking-widest">Customer & Pin</th>
                  <th className="px-6 py-4 text-left text-[11px] font-black text-gray-400 uppercase tracking-widest">AWB Number</th>
                  <th className="px-6 py-4 text-left text-[11px] font-black text-gray-400 uppercase tracking-widest">Deduction (₹)</th>
                  <th className="px-6 py-4 text-center text-[11px] font-black text-gray-400 uppercase tracking-widest">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {loading ? (
                    <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-gray-500 italic">Loading Delhivery Data...</td></tr>
                ) : filteredOrders.length > 0 ? (
                  filteredOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 bg-white dark:bg-gray-800 transition-colors">
                      <td className="px-6 py-4">
                        <p className="text-xs font-mono font-black text-gray-600 dark:text-gray-300">{String(order.id).substring(0, 8)}</p>
                        <p className="text-[9px] text-gray-400 uppercase tracking-widest">{order.source}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-black text-gray-900 dark:text-white">{order.customerName}</p>
                        <p className="text-[10px] font-bold text-gray-500">PIN: {order.pincode}</p>
                      </td>
                      <td className="px-6 py-4">
                         <span className="text-sm font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-1 rounded">
                             {order.waybill}
                         </span>
                      </td>
                      
                      <td className="px-6 py-4 text-left">
                        {order.deliveryCharge > 0 ? (
                            <div>
                                <p className="text-lg font-black text-red-600 dark:text-red-400 tracking-tight">- ₹{order.deliveryCharge.toFixed(2)}</p>
                                <p className="text-[9px] text-gray-400">Delhivery Charge</p>
                            </div>
                        ) : (
                            <button 
                                onClick={() => handleSyncCharge(order.id, order.pincode, order.paymentMode, order.totalAmount)}
                                disabled={syncingId === order.id || isBulkSyncing}
                                className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm disabled:opacity-50"
                            >
                                <FaSyncAlt className={`${syncingId === order.id ? 'animate-spin' : ''}`} />
                                {syncingId === order.id ? 'Fetching...' : 'Sync Charge'}
                            </button>
                        )}
                      </td>

                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => { setSelectedOrder(order); setShowDetailsModal(true); }}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full"
                          title="View Details"
                        >
                          <FaEye className="inline-block text-lg" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center text-gray-500 dark:text-gray-400 font-bold">
                      No shipped orders found with waybills.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showDetailsModal && selectedOrder && (
        <OrderDetailsModal order={selectedOrder} onClose={() => { setShowDetailsModal(false); setSelectedOrder(null); }} />
      )}
    </AdminLayout>
  );
};

export default DeliveryChargePage;